<template>
  <div class="doc-thumb" :style="{ width: `${size}px`, height: `${size}px` }" :title="is360 ? '360° image' : null">
    <img
      v-if="src && !broken"
      class="doc-thumb__img"
      :src="src"
      alt=""
      loading="lazy"
      decoding="async"
      @load="onLoad"
      @error="onError" />
    <span v-else-if="fallback === 'icon'" class="doc-thumb__icon fiv-viv" :class="`fiv-icon-${extension || 'blank'}`" aria-hidden="true"></span>
    <span v-else class="doc-thumb__ext">{{ extensionLabel }}</span>
    <span v-if="is360 && src && !broken" class="doc-thumb__badge" aria-label="360° image">360°</span>
  </div>
</template>

<script>
  import { DOCUMENT_KIND, resolveKind, documentExtension, documentRenderKey } from '../viewer/documentKinds';
  import { resolveDocumentUrl } from '../viewer/documentSource';
  import { isEquirectangularRatio } from '../viewer/panoramaDetection';

  /**
   * A document's picture for cards and lists. The API makes a 256 px thumbnail for PDFs and images
   * after upload (`thumbnailUrl`); until it exists, or if it cannot be loaded, an image document
   * shows the image itself through a fresh signed link (lazy, so only visible tiles download).
   * Anything else shows the file's extension or its file-type icon (`fallback`). A 2:1 picture
   * gets a 360° badge, since that is how panoramas are told apart.
   */
  export default {
    name: 'DocumentThumbnail',
    props: {
      /** DocumentVM-like: fileName, thumbnailUrl, storageReference */
      document: { type: Object, required: true },
      size: { type: Number, default: 50 },
      /** What to show without a picture: the extension text or the file-type icon. */
      fallback: { type: String, default: 'extension' },
    },
    data() {
      return {
        src: null,
        broken: false,
        is360: false,
        triedFile: false,
      };
    },
    computed: {
      renderKey() {
        return `${documentRenderKey(this.document)}|${this.document.thumbnailUrl || ''}`;
      },
      isImage() {
        return resolveKind(this.document) === DOCUMENT_KIND.IMAGE;
      },
      extension() {
        return documentExtension(this.document);
      },
      extensionLabel() {
        const ext = this.extension.toUpperCase();
        return ext ? ext.slice(-4) : 'FILE';
      },
    },
    watch: {
      renderKey: {
        immediate: true,
        handler() {
          this.reset();
        },
      },
    },
    methods: {
      reset() {
        this.broken = false;
        this.is360 = false;
        this.triedFile = false;
        this.src = this.document.thumbnailUrl || null;
        if (!this.src) this.useFile();
      },
      /** No usable thumbnail: an image is its own thumbnail; anything else gets the fallback. */
      async useFile() {
        if (this.triedFile || !this.isImage) {
          this.broken = true;
          return;
        }
        this.triedFile = true;
        const key = this.renderKey;
        try {
          const url = await resolveDocumentUrl(this.document);
          if (key === this.renderKey) this.src = url;
        } catch (err) {
          if (key === this.renderKey) this.broken = true;
        }
      },
      onLoad(event) {
        const img = event.target;
        this.is360 = this.isImage && isEquirectangularRatio(img.naturalWidth, img.naturalHeight);
      },
      onError() {
        this.useFile();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .doc-thumb {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    overflow: hidden;
    border-radius: var(--ds-radius-md, 8px);
    background: var(--v-bg1-base, #f1f2f4);
  }

  .doc-thumb__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .doc-thumb__icon {
    font-size: 36px;
    line-height: 1;
  }

  .doc-thumb__ext {
    font-size: 13px;
    font-weight: 600;
    color: #6c6c6c;
  }

  .doc-thumb__badge {
    position: absolute;
    right: 2px;
    bottom: 2px;
    padding: 0 4px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
    line-height: 14px;
    color: #fff;
    background: rgba(0, 0, 0, 0.65);
  }
</style>
